import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import cx from 'classnames';
import { useAtom } from 'jotai';
import { useQueryClient } from 'react-query';

import Button from '~/components/button';
import CancelBadge from '~/components/plans/bundles-page/cancel-badge';
import DesktopPlans from '~/components/plans/bundles-page/desktop-plans';
import FullWidthSection from '~/components/static/full-width-section';
import LoadingIndicator from '~/components/loading-indicator';
import MobilePlanCarousel from '~/components/plans/bundles-page/mobile-plan-carousel';
import PromoBanner from '~/components/static/promo-banner/promo-banner';
import PurchaseCta from '~/components/plans/bundles-page/purchase-cta';
import Shipment from '~/components/shipments/shipment';
import SmartBadges from '~/components/plans/bundles-page/smart-badges';
import BackToTopPill from '~/components/back-to-top-pill/back-to-top-pill';
import {
  PlanDescription,
  PurchaseCtaPrice,
} from '~/components/plans/bundles-page/plan-bundle-page-components';
import { usePromoBanner } from '~/hooks/use-promo-banner';
import { useLawnBundles, useLawnByUuid } from '~/features/lawns/lawn-actions';
import { useModal } from '~/features/modals/modal-actions';
import { useMediaQuery } from '~/hooks/use-media-query';
import { useAddBundleToCart } from '~/components/plans/bundles-page/use-add-bundle-to-cart';
import { useShippingMessages } from '~/hooks/use-shipping-messages';
import {
  getRecommendedLawnPlanIndex,
  PAYMENT_TYPE,
} from '~/components/plans/bundles-page/bundles';
import { sourceEngine } from '~/features/plans/plan';
import { newLawnRoute } from '~/routes';
import {
  IS_FLEX_PAY_ENABLED_FLAG,
  IS_FIRST_APPLICATION_TRIAL_ENABLED_FLAG,
  IS_PRE_SEASON_FLAG,
  IS_SEASON_TRUNCATED_FLAG,
  ARE_TIERED_PLAN_DISCOUNTS_ENABLED_FLAG,
} from '~/feature-flags';
import { addDays, toMonthAndYear } from '~/utils/dates';
import { captureException } from '~/utils/exception-tracking';
import { centsToCurrency, formatNumber } from '~/utils/numbers';
import { useVwoExperiment } from '~/hooks/use-vwo-experiment';
import { useInterval } from '~/hooks/use-interval';
import { useFormAnswers } from '~/hooks/use-form-answers';
import {
  APPLICATION_TIMELINE_TEST,
  GRASS_FUNNEL_TEST,
  HARDMARK_TEST,
  LAWN_PSP_HIDE_HERB_POUCH_SPRAYER_TEST,
  LAWN_SIZE_TEST,
  MOBILE_STICKY_TEST,
  NEED_HELP_FOOTER_TEST,
  PDP_MODAL_TEST,
  PRODUCT_TOOLTIP_TEST,
  SMART_BADGES_TEST,
  WEED_WARRIOR_FUNNEL_TEST,
  YARD_CONTINUATION_TEST,
} from '~/utils/vwo-tests';
import { EventType, analyticsBeacon } from '~/utils/analytics';
import { NeedHelpFooter } from '~/components/seed/seed-finder-results-page';
import SmartLawnPlanReviewsV2 from '~/components/static/reviews/smart-lawn-plan-reviews-v2';
import { addProductAtom } from '~/components/lawns/questionnaire/weed-warrior-interstitial';
import { useCartMutations } from '~/features/cart/cart-actions';
import { useProductData } from '~/features/products/products-actions';
import { subscriptionType } from '~/features/subscriptions/subscription';
import FaqModalContent from '~/components/modals/plan-faqs/lawn-plan-faq-content';
import { useFlashMessage } from '~/features/flash-messages/flash-message-actions';
import { cookies } from '~/utils/cookie';
import ApplicationsTimeline from '~/components/plans/bundles-page/applications-timeline';
import { useTitle } from '~/hooks/use-title';
import { filterOutHiddenItems } from '~/features/shipments/shipment';
import { addTieredDiscountCoupon } from '~/features/cart/cart';
import { navigateToNextFunnelPage } from '~/components/plans/helpers';

import styles from '~/components/plans/bundles-page/plan-bundle-page.module.scss';
import buttonStyles from '~/components/button.module.scss';
import errorStyles from '~/components/error/error.module.scss';
import faqStyles from '~/components/modals/plan-faqs/plan-faqs-modal.module.scss';
import flashStyles from '~/components/messages/flash-message.module.scss';

export const seasonalCopy = IS_SEASON_TRUNCATED_FLAG
  ? ''
  : ' total for the year';

const PLAN_DESC_COPY = [
  'Best for keeping your lawn green and healthy.',
  'Best for already lush lawns that have weeds.',
  'Best for lawns with bare patches or thinning grass and weeds.',
  'Best for lawns that need to stand up to rough and tumble activity of kids or pets.',
];

const PlanBundlePage = () => {
  useTitle('Select your lawn plan');
  const { lawnUuid } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { showModal } = useModal();
  const { showFlashMessage } = useFlashMessage();
  const { promoBanner, isLoading: isLoadingBanner } =
    usePromoBanner('/lawn-plan');
  const { formAnswers, isLoading: isLoadingFormAnswers } = useFormAnswers();
  const { lawn, isLoading: isLoadingLawn } = useLawnByUuid(lawnUuid);

  const lawnQuizAnswers = formAnswers?.LAWN_FUNNEL?.[0]?.answers;

  const { variant: hardmarkVariant } = useVwoExperiment(HARDMARK_TEST.NAME);
  const showHardmarkPricing =
    hardmarkVariant === HARDMARK_TEST.HARDMARK_VARIANT;

  const { variant: productDetailsModalVariant } = useVwoExperiment(
    PDP_MODAL_TEST.NAME
  );
  const showProductDetailsModal =
    window.env.SUNDAY_ENV === 'modal-test' ||
    productDetailsModalVariant === PDP_MODAL_TEST.SHOW_DETAIL_MODAL_VARIANT;

  const { variant: badgeVariant } = useVwoExperiment(SMART_BADGES_TEST.NAME);

  const { variant: lawnSizeVariant } = useVwoExperiment(LAWN_SIZE_TEST.NAME);
  const showLawnSizeModal =
    lawnSizeVariant === LAWN_SIZE_TEST.LAWN_SIZE_VARIANT;

  const { variant: productTooltipVariant } = useVwoExperiment(
    PRODUCT_TOOLTIP_TEST.NAME
  );
  const showAltTooltip =
    window.env.SUNDAY_ENV === 'tooltip' ||
    productTooltipVariant === PRODUCT_TOOLTIP_TEST.TOOLTIP_COPY_VARIANT;

  const { variant: needHelpFooterVariant } = useVwoExperiment(
    NEED_HELP_FOOTER_TEST.NAME
  );
  const showNeedHelpFooter =
    needHelpFooterVariant === NEED_HELP_FOOTER_TEST.SHOW_NEED_HELP_VARIANT;

  const { variant: applicationTimelineVariant } = useVwoExperiment(
    APPLICATION_TIMELINE_TEST.NAME
  );
  const showApplicationTimeline = [
    APPLICATION_TIMELINE_TEST.LAWN_TIMELINE_VARIANT,
    APPLICATION_TIMELINE_TEST.YARD_TIMELINE_VARIANT,
  ].includes(applicationTimelineVariant);

  const { getVariantName: getYardContinuationVariantName } = useVwoExperiment(
    YARD_CONTINUATION_TEST.NAME,
    { activateOnLoad: false }
  );
  const skipContinuationPage =
    getYardContinuationVariantName() ===
    YARD_CONTINUATION_TEST.SKIP_PAGES_VARIANT;

  const { getVariantName: getWeedVariantName } = useVwoExperiment(
    WEED_WARRIOR_FUNNEL_TEST.NAME,
    { activateOnLoad: false }
  );

  const showWeedProductVariant = [
    WEED_WARRIOR_FUNNEL_TEST.FUNNEL_PSP_VARIANT,
    WEED_WARRIOR_FUNNEL_TEST.PSP_ONLY_VARIANT,
    WEED_WARRIOR_FUNNEL_TEST.YARD_FUNNEL_PSP_VARIANT,
  ].includes(getWeedVariantName());

  const [{ weedWarriorSelected }] = useAtom(addProductAtom);

  const { getVariantName: getGrassQuizVariant } = useVwoExperiment(
    GRASS_FUNNEL_TEST.NAME,
    { activateOnLoad: false }
  );
  const lawnGrassTypeValue = lawnQuizAnswers?.find(
    (q) => q.question === 'grass-type'
  )?.response;
  const showNonSeedPlan =
    getGrassQuizVariant() === GRASS_FUNNEL_TEST.GRASS_QUESTION_VARIANT &&
    lawn?.climate?.grassType === 'cool' &&
    lawnGrassTypeValue !== 'fescue';

  if (location.pathname === '/lawns/:lawnUuid/lawn-plan') {
    captureException(new Error('Missing lawnUuid on PSP'), {
      extras: {
        lawnUuid,
      },
    });
    // Delay navigation until error is captured on this page
    setTimeout(() => {
      navigate(newLawnRoute());
    }, 500);
  }

  const isDesktop = useMediaQuery('(min-width: 1200px)');
  const isTablet = useMediaQuery('(min-width: 768px)');

  const { addBundleToCart, isAddingToCart } = useAddBundleToCart({ lawnUuid });
  const { addToCart } = useCartMutations();
  const { product: weedWarriorProduct } = useProductData({
    slug: 'weed-warrior-starter-pack',
  });
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(2);
  const [animationHasFinished, setAnimationHasFinished] = useState(false);
  const [isPillVisible, setIsPillVisible] = useState(false);
  const queryClient = useQueryClient();

  const isYardFunnelEnabled = true;

  // Logic for <BackToTopPill /> must live on parent
  useEffect(() => {
    const handleScroll = () => {
      const screenHeight = window.screen.availHeight;
      const scrollTop = window.scrollY;

      if (scrollTop > screenHeight) {
        setIsPillVisible(true);
      } else setIsPillVisible(false);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  let recommendedPlanIndex = getRecommendedLawnPlanIndex(lawnQuizAnswers);
  if (showNonSeedPlan) {
    recommendedPlanIndex = 1;
  }
  useEffect(() => {
    if (recommendedPlanIndex) {
      setSelectedPlanIndex(recommendedPlanIndex);
    }
  }, [recommendedPlanIndex]);

  const {
    variant: fullMobileStickyVariant,
    activate: activateFullMobileSticky,
  } = useVwoExperiment(MOBILE_STICKY_TEST.NAME, {
    activateOnLoad: false,
  });

  // During final season truncation, we only offer pay in full
  const paymentType = IS_FLEX_PAY_ENABLED_FLAG
    ? PAYMENT_TYPE.FLEX_PAY
    : PAYMENT_TYPE.PAY_IN_FULL;

  const {
    bundles: rawBundles,
    isLoading: isLoadingBundles,
    isFetched: areBundlesFetched,
    error: lawnBundlesError,
  } = useLawnBundles(lawnUuid, {
    firstApplicationTrial: IS_FIRST_APPLICATION_TRIAL_ENABLED_FLAG,
    paymentType,
  });

  const { variant } = useVwoExperiment(
    LAWN_PSP_HIDE_HERB_POUCH_SPRAYER_TEST.NAME
  );

  const hideHerbicidePouchAndSprayer =
    variant === LAWN_PSP_HIDE_HERB_POUCH_SPRAYER_TEST.HIDE_SPRAYER_VARIANT;

  const yardIssuesCookie = cookies?.get('YARD_ISSUES');

  const flashMessageShown = useRef(false);

  useEffect(() => {
    if (
      areBundlesFetched &&
      skipContinuationPage &&
      !flashMessageShown.current
    ) {
      let nextPlan;
      let messageType;
      if (
        yardIssuesCookie?.includes('pest') &&
        formAnswers?.YARD_PEST_FUNNEL?.length > 0
      ) {
        flashMessageShown.current = true;
        nextPlan = 'pest plan';
        messageType = 'pest';
      } else if (
        yardIssuesCookie?.includes('garden') &&
        formAnswers?.GARDEN_FUNNEL?.length > 0
      ) {
        flashMessageShown.current = true;
        nextPlan = 'garden plan';
        messageType = 'garden';
      }

      if (nextPlan && messageType) {
        const timeoutId = setTimeout(() => {
          showFlashMessage(
            <>
              <div className={flashStyles.messageHeading}>
                You'll select a {nextPlan} next...
              </div>
              <em>after you select a lawn plan</em>
            </>,
            messageType,
            3000
          );
        }, 1000);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [
    areBundlesFetched,
    formAnswers?.GARDEN_FUNNEL?.length,
    formAnswers?.YARD_PEST_FUNNEL?.length,
    showFlashMessage,
    skipContinuationPage,
    yardIssuesCookie,
  ]);

  // Because we want the tabs in the Plan Details section to
  // match with the selected card, we're controlling the
  // slide index explicitly at the page level and doing
  // the animation here.
  useInterval(
    () => {
      if (
        fullMobileStickyVariant !== MOBILE_STICKY_TEST.VARIANT ||
        animationHasFinished ||
        !rawBundles
      ) {
        return;
      }
      if (selectedPlanIndex < rawBundles?.length - 1) {
        setSelectedPlanIndex(selectedPlanIndex + 1);
      } else {
        setSelectedPlanIndex(rawBundles?.length - 1);
        setAnimationHasFinished(true);
      }
    },
    fullMobileStickyVariant === MOBILE_STICKY_TEST.VARIANT &&
      !animationHasFinished
      ? 1200
      : null
  );

  useEffect(() => {
    if (!isDesktop) {
      activateFullMobileSticky();
    }
  }, [activateFullMobileSticky, isDesktop]);

  const isFlexPay = paymentType === PAYMENT_TYPE.FLEX_PAY;

  const isDryFert = rawBundles?.some(
    (bundle) => bundle?.sourceEngine === sourceEngine.GRANULAR
  );

  /**
   * Lawn Plan SKUs have multiple features so we have to get a tad clever:
   * we create hard-coded Lawn Plan-specific features and place them at the
   * beginning of the returned feature list
   */
  const bundles = rawBundles?.map((bundle) => {
    const pouchFeature = {
      quantity: bundle.lawnPlanPouchCount, // used for liquid and granular Lawn Plans
      text: isDryFert ? 'Granular fertilizer bags' : 'Fertilizer pouches',
      included: true,
      icon: isDryFert ? 'seedBag' : 'pouch',
    };

    const features = [
      pouchFeature,
      ...(bundle?.bundleItems?.filter((feature) => Boolean(feature.text)) ||
        []),
    ];

    return { ...bundle, features };
  });

  if (!lawnBundlesError && areBundlesFetched && !bundles) {
    captureException(new Error('Missing plan bundles data on PSP'), {
      extras: {
        lawnUuid,
      },
    });
  }

  const selectedBundle = bundles ? bundles[selectedPlanIndex] : null;
  const recommendedBundle = bundles ? bundles[recommendedPlanIndex] : null;

  const isRecommended = selectedPlanIndex === recommendedPlanIndex;

  if (bundles?.length > 0 && !selectedBundle) {
    captureException(new Error('No selectedBundle on PSP'), {
      extras: {
        lawnUuid,
        bundles,
      },
    });
  }

  useEffect(() => {
    if (recommendedBundle?.uuid) {
      analyticsBeacon.emit(EventType.PLAN_TIER_RECOMMENDED, {
        flexPayFirstBoxPrice: recommendedBundle.carts?.[0]?.subtotal,
        planName: recommendedBundle.title,
        planPrice: recommendedBundle.fullSeasonPrice,
        planSku: recommendedBundle.lawnPlanBundleItem.skuId,
        planUuid: recommendedBundle.uuid,
      });
    }
  }, [
    recommendedBundle?.carts,
    recommendedBundle?.fullSeasonPrice,
    recommendedBundle?.lawnPlanBundleItem,
    recommendedBundle?.title,
    recommendedBundle?.uuid,
  ]);

  const handleFooterAddToCart = async () => {
    await addBundleToCart(
      selectedBundle,
      isFlexPay,
      isRecommended,
      skipContinuationPage
    );

    const isTieredPlanDiscountsEnabled = ARE_TIERED_PLAN_DISCOUNTS_ENABLED_FLAG;
    if (isTieredPlanDiscountsEnabled) {
      await addTieredDiscountCoupon(
        selectedBundle?.selectedCartUuid,
        addTieredDiscountCoupon,
        queryClient,
        showFlashMessage
      );
    }

    // If weedWarriorSelected is true for test users, add SKU to cart
    if (showWeedProductVariant && weedWarriorSelected) {
      addToCart(weedWarriorProduct?.purchaseSku, 1, {
        addToSubscription: true,
        cartUuidToUse: selectedBundle?.selectedCartUuid,
        subscriptionType: subscriptionType.BASIC,
      });
    }

    navigateToNextFunnelPage(
      isYardFunnelEnabled,
      skipContinuationPage,
      lawnUuid,
      navigate
    );
  };

  const shipmentsWithBoxNumbers = selectedBundle?.carts.map((cart, index) => {
    // reduce the shipment items to an array
    const boxNumber = index + 1;
    let shipmentItems = cart.items.map((s) => s.shipmentItems).flat();
    if (hideHerbicidePouchAndSprayer) {
      shipmentItems = filterOutHiddenItems(shipmentItems);
    }

    return {
      ...cart,
      shipmentItems,
      boxNumber,
      boxDescription: getBoxDescription(boxNumber, selectedPlanIndex),
    };
  });

  const lawnPlanSku = selectedBundle?.bundleItems.find(
    (sku) => !sku.title.includes('Smart Lawn Plan')
  );
  const { shippingMessage, isLoadingProviders } = useShippingMessages(
    lawnPlanSku?.skuId
  );

  if (lawnBundlesError) {
    return (
      <div>
        <div className={styles.errorContainer}>
          <img
            className={errorStyles.img}
            src="/images/smoking-mower.gif?v1"
            alt="A lawn mower with smoke coming out"
            width="457"
            height="536"
          />

          <h1 className={errorStyles.title}>Shucks</h1>

          <section className={errorStyles.section}>
            <p className={errorStyles.copy}>Something went wrong.</p>
            <p className={errorStyles.copy}>
              Try reloading the page, or start over by entering your lawn
              address.
            </p>
          </section>
          <a
            className={cx(buttonStyles.button, buttonStyles.outline)}
            href={newLawnRoute()}
          >
            Start over
          </a>
        </div>
      </div>
    );
  }

  const isLoading =
    !lawn ||
    isLoadingLawn ||
    !rawBundles ||
    isLoadingBundles ||
    isLoadingProviders ||
    isLoadingFormAnswers;

  if (isLoading) {
    return (
      <div style={{ height: '100vh' }}>
        <div className={styles.loadingContainer}>
          <h1>Building your plan</h1>
          <LoadingIndicator />
        </div>
      </div>
    );
  }
  // const firstBoxShipDate = toMonthAndOrdinal(
  //   derivedShipmentDate(shipmentsWithBoxNumbers[0])
  // );

  const makeCtaTagline = (isFlexPay) => {
    // fullSeasonDiscountedPrice is selectedBundle?.fullSeasonPrice + the discount applied
    // if there is not discount, fullSeasonDiscountedPrice is the same as selectedBundle?.fullSeasonPrice
    if (isFlexPay) {
      if (IS_SEASON_TRUNCATED_FLAG) {
        if (selectedBundle?.carts?.length === 1) {
          return null;
        }
        return `${centsToCurrency(selectedBundle?.fullSeasonDiscountedPrice, {
          round: true,
        })} total for this year.`;
      } else {
        return `${centsToCurrency(selectedBundle?.fullSeasonDiscountedPrice, {
          round: true,
        })}${seasonalCopy}`;
      }
    }

    return null;
  };

  const subHeadingText = showLawnSizeModal ? (
    <>
      Tailored to your soil, climate, and yard condition at{' '}
      <Button
        className={styles.lawnSizeTrigger}
        onClick={() => showModal('LAWN_SIZE', { lawn, isDryFert })}
        variant="link"
      >
        {formatNumber(lawn?.size?.sqFt)} sq. ft.{' '}
        <img
          src="/icons/editable.svg"
          alt="pencil to edit"
          height={24}
          width={24}
        />
      </Button>{' '}
      <br className={styles.subheaderBreak} />
      (all delivered for free).
    </>
  ) : (
    <>
      Tailored to your soil, climate, and yard <span>condition</span> at{' '}
      <strong>{formatNumber(lawn?.size?.sqFt)} sq. ft.</strong> — all delivered
      for free.
    </>
  );

  return (
    <div
      className={styles.planBundlePage}
      data-testid="plan-bundle-page"
      id="lawn-plan-selection-page"
    >
      <section id="plan-details" className={styles.planDetailsSection}>
        <h1 className={styles.headline} id="lawn-psp-heading">
          Custom lawn plan ready!
        </h1>
        <p className={styles.planCoverageText} id="lawn-psp-subheading">
          {subHeadingText}
        </p>

        <ul className={styles.planSelector} id="plan-tabs-container">
          {bundles.map((bundle, index) => (
            <li key={bundle.uuid}>
              <button
                onClick={() => setSelectedPlanIndex(index)}
                className={cx(styles.planSelectorLink, {
                  [styles.selected]: selectedPlanIndex === index,
                })}
              >
                {bundle.title}
              </button>
            </li>
          ))}
        </ul>

        {badgeVariant === SMART_BADGES_TEST.SHOW_BADGES_VARIANT && (
          <SmartBadges
            lawn={lawn}
            fertilizerType={bundles?.[0]?.sourceEngine}
            selectedBundleTitle={selectedBundle?.title}
          />
        )}

        <PurchaseCta
          isLoading={isAddingToCart}
          mainContent={
            <PlanDescription
              imageSrc={
                isDryFert
                  ? '/images/dry-fert-thumb.jpg'
                  : '/images/unboxing-bottom-400w.png?v1'
              }
              imageAlt="Custom lawn plan package unboxing"
              badgeText={IS_PRE_SEASON_FLAG && 'PRE-ORDER'}
              planTitle={selectedBundle?.title}
              planDescription={
                <>
                  {PLAN_DESC_COPY[selectedPlanIndex]}{' '}
                  <Button
                    className={faqStyles.faqTriggerButton}
                    variant="link"
                    onClick={() => showModal('PLAN_FAQS')}
                  >
                    See FAQs
                  </Button>
                </>
              }
            />
          }
          disclaimerText="Fertilizer selection and timing may be updated based on weather and soil
          results. Subscription contents are not adjustable at this time. Yearly plan renews every spring."
          priceComponent={
            <PurchaseCtaPrice
              isFlexPay={isFlexPay}
              numOfShipments={selectedBundle?.carts?.length}
              selectedBundle={selectedBundle}
              showHardmarkPricing={showHardmarkPricing}
              showSoilTestDiscount={lawn?.hasSoilTest}
            />
          }
          buttonText={
            IS_PRE_SEASON_FLAG ? 'Pre-order for spring' : 'Add to cart'
          }
          tagline={makeCtaTagline(isFlexPay)}
          onClick={() => handleFooterAddToCart()}
          showReviews
          reviewsLinkAnchor="#reviews-section"
          firstShipmentDate={
            IS_PRE_SEASON_FLAG && selectedBundle?.carts?.[0]?.shipDate
          }
        />

        {isPillVisible && <BackToTopPill />}

        {showApplicationTimeline && (
          <ApplicationsTimeline
            climate={lawn?.climate}
            selectedBundle={selectedBundle}
          />
        )}

        <div className={styles.shipmentsSpacing} id="plan-shipments-container">
          {shipmentsWithBoxNumbers?.map((shipment, i) => (
            <Shipment
              key={`${shipment.uuid}-${shipment.boxNumber}`}
              shipment={shipment}
              panelHeaderContent={
                <div className={styles.shipmentDate}>
                  Ships in{' '}
                  {determineShippingText(
                    shipment,
                    shippingMessage,
                    shipment.boxNumber
                  )}
                </div>
              }
              showProductDetailsModal={showProductDetailsModal}
              showPrice={true}
              id={`shipment-${i}`}
              faqContent={
                shipmentsWithBoxNumbers.length === i + 1 && FaqModalContent
              }
              showAltTooltip={showAltTooltip}
              showWeedProductVariant={showWeedProductVariant}
            />
          ))}
        </div>

        <div className={styles.ctaWrapper}>
          <PurchaseCta
            isLoading={isAddingToCart}
            mainContent={
              <PlanDescription
                imageSrc={
                  isDryFert
                    ? '/images/dry-fert-thumb.jpg'
                    : '/images/unboxing-bottom-400w.png?v1'
                }
                imageAlt="Custom lawn plan package unboxing"
                badgeText={IS_PRE_SEASON_FLAG && 'PRE-ORDER'}
                planTitle={selectedBundle?.title}
                planDescription={
                  <>
                    {PLAN_DESC_COPY[selectedPlanIndex]}{' '}
                    <Button
                      className={faqStyles.faqTriggerButton}
                      variant="link"
                      onClick={() => showModal('PLAN_FAQS')}
                    >
                      See FAQs
                    </Button>
                  </>
                }
              />
            }
            disclaimerText="Fertilizer selection and timing may be updated based on weather and soil
          results. Subscription contents are not adjustable at this time. Yearly plan renews every spring."
            priceComponent={
              <PurchaseCtaPrice
                isFlexPay={isFlexPay}
                numOfShipments={selectedBundle?.carts?.length}
                selectedBundle={selectedBundle}
                showHardmarkPricing={showHardmarkPricing}
                showSoilTestDiscount={lawn?.hasSoilTest}
              />
            }
            buttonText={
              IS_PRE_SEASON_FLAG ? 'Pre-order for spring' : 'Add to cart'
            }
            tagline={makeCtaTagline(isFlexPay)}
            onClick={() => handleFooterAddToCart()}
            showReviews
            reviewsLinkAnchor="#reviews-section"
            firstShipmentDate={
              IS_PRE_SEASON_FLAG && selectedBundle?.carts?.[0]?.shipDate
            }
          />
        </div>

        <CancelBadge />
      </section>

      <PromoBanner
        className={styles.promoBannerContainer}
        content={promoBanner}
        isLoadingContent={isLoadingBanner}
      />

      <section className={styles.bundlesSection}>
        <h2 className={styles.planDetailsTitle}>Compare plans</h2>
        <p className={styles.planDetailsSubtitle}>
          {isDryFert ? (
            <span>
              For larger lawns like yours, we recommend our granular
              fertilizers. They're powered by nitrogen and nutrient-rich
              biostimulants—and formulated to easily cover large areas.{' '}
              <strong>For best results, apply using a spreader.</strong>
            </span>
          ) : (
            'Each plan option has been customized to fit your soil, climate, and lawn needs.'
          )}
        </p>

        <DesktopPlans
          bundles={bundles}
          onSelect={setSelectedPlanIndex}
          isFlexPay={isFlexPay}
          showHardmarkPricing={showHardmarkPricing}
          showSoilTestDiscount={lawn?.hasSoilTest}
          isRecommended={isRecommended}
          showFirstShipmentDate={IS_PRE_SEASON_FLAG}
          ctaText={IS_PRE_SEASON_FLAG ? 'Pre-order for  spring' : undefined} // use default if not pre-season
        />

        {!isDesktop && (
          <MobilePlanCarousel
            bundles={bundles}
            isFlexPay={isFlexPay}
            setSelectedPlanIndex={setSelectedPlanIndex}
            selectedPlanIndex={selectedPlanIndex}
            isTablet={isTablet}
            animateOnLoad={true}
            showHardmarkPricing={showHardmarkPricing}
            showSoilTestDiscount={lawn?.hasSoilTest}
            isRecommended={isRecommended}
            showFirstShipmentDate={IS_PRE_SEASON_FLAG}
            ctaText={IS_PRE_SEASON_FLAG ? 'Pre-order for  spring' : undefined} // use default text if not pre-season
          />
        )}
      </section>

      {showNeedHelpFooter && (
        <div className={styles.helpFooterWrapper}>
          <NeedHelpFooter />
        </div>
      )}

      <FullWidthSection backgroundColor="var(--light-color)">
        <h2 className={styles.reviewsHeading} id="reviews-section">
          Real people, real results
        </h2>
        <SmartLawnPlanReviewsV2 />
      </FullWidthSection>
    </div>
  );
};

export default PlanBundlePage;

export const determineShippingText = (shipment, shippingMessage, idx) => {
  const today = new Date();
  const todayPlusTen = addDays(today, 10);
  if (shipment.boxNumber === 1 || idx === 0) {
    return new Date(shipment.shipDate) > todayPlusTen
      ? toMonthAndYear(shipment.shipDate)
      : shippingMessage?.message;
  } else {
    return toMonthAndYear(shipment.shipDate);
  }
};

export function getBoxDescription(boxNumber, planIndex) {
  switch (boxNumber) {
    case 1:
      return "Start your lawn's transformation with perfectly timed spring nutrients and soil testing tools.";
    case 2:
      // For "Basic Care", don't use add-on language
      if (planIndex === 0) {
        return 'Power through peak summer with custom nutrients that keep your lawn strong and green.';
        // For "Pets" bundle, use pet-specific language
      } else if (planIndex === 3) {
        return "Your next box delivers everything needed for a lawn that stands up to pets and play. Once dry it's safe to play.";
      } else {
        return "Your next box brings everything needed for a thicker, weed-free lawn this season. Once dry it's safe to play.";
      }
    case 3:
      // For "Basic Care", use last box language
      if (planIndex === 0) {
        return 'Close the season right with nutrients to sustain green, build winter resilience, and ready for spring.';
      } else {
        return 'Power through peak summer with custom nutrients that keep your lawn strong and green.';
      }
    default:
      return 'Close the season right with nutrients to sustain green, build winter resilience, and ready for spring.';
  }
}
